import { API_ROUTE } from '../../config';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Calendar as UICalendar } from '../ui/calendar';
import { toast } from 'react-toastify';

// 1. Типізація відповіді API
export type CalendarApiService = {
  Date: string; // ISO string
  Rows: Array<{
    Time: string;
    DoctorId: number;
    DoctorFullName: string;
    Price: number;
    OfficeId: number;
    Services: Array<{
      IsOnline: boolean;
      Id: number;
      Name: string;
    }>;
    MinimumPrepaymentAmount: number | null;
    FacilityId: number;
  }>;
};

interface CalendarMobileViewProps {
  schedule: CalendarApiService[];
  value: Date | null;
  onChange: (date: Date | null) => void;
  productId: number | undefined;
  doctorId?: string;
}

export default function CalendarMobileViewOnline({ value, onChange, productId, doctorId, schedule }: CalendarMobileViewProps) {
  const [calendarKey, setCalendarKey] = useState(0);

  // Автоматичний вибір першої доступної дати при зміні лікаря
  useEffect(() => {
    if (Array.isArray(schedule)) {
      const availableDates = getFilteredDates();
      
      if (availableDates.length > 0) {
        // Сортуємо дати за зростанням
        const sortedDates = [...availableDates].sort((a, b) => a.getTime() - b.getTime());
        
        // Якщо поточна дата недоступна або не вибрана, вибираємо першу доступну
        if (!value || !isDayAvailable(value)) {
          const firstAvailableDate = sortedDates[0];
          if (firstAvailableDate) {
            onChange(firstAvailableDate);
          }
        }
      }
    }
  }, [doctorId, schedule]);

  // Скидання вибраної дати, якщо вона не входить у доступні
  useEffect(() => {
    if (value && !isDayAvailable(value)) {
      onChange(null);
    }
    setCalendarKey(prevKey => prevKey + 1);
    // eslint-disable-next-line
  }, [doctorId]);

  // Функція для фільтрації дат по doctorId
 const getFilteredDates = () => {
  if (!Array.isArray(schedule)) {
    return [];
  }

  const now = new Date();

  return (schedule as CalendarApiService[])
    .filter((item) => {
      const itemDate = new Date(item.Date);
      // Normalize 'now' to the start of today for date-only comparisons
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      // A date is valid if it's today or in the future
      if (itemDate < today) {
        return false;
      }

      const validRows = item.Rows.filter((row) => {
        const doctorMatch =
          !doctorId ||
          doctorId === '' ||
          String(row.DoctorId) === String(doctorId);

        // The time of the slot must be in the future
        const time = new Date(row.Time);
        const timeValid = time > now;

        return doctorMatch && timeValid;
      });

      return validRows.length > 0;
    })
    .map((item) => new Date(item.Date));
};

  const isDayAvailable = (date: Date) => {
    const filteredDates = getFilteredDates();
    return filteredDates.some(
      (d) =>
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate()
    );
  };

  const handleMonthChange = (month: Date) => {
    const availableDates = getFilteredDates();

    const datesInMonth = availableDates.filter(
      (d) => d.getFullYear() === month.getFullYear() && d.getMonth() === month.getMonth()
    );

    if (datesInMonth.length > 0) {
      datesInMonth.sort((a, b) => a.getTime() - b.getTime());
      onChange(datesInMonth[0]);
    } else {
      onChange(null);
    }
  };

  return (
    <div className="w-full">
     
      <h3 className='mb-2 text-[16px] font-semibold text-center sm:text-left'>
      Wybierz dzień i czas
      </h3>
      <UICalendar
        key={calendarKey}
        mode="single"
        selected={value || undefined}
        onSelect={(date) => onChange(date ?? null)}
        onMonthChange={handleMonthChange}
        className="rounded-[15px] py-2 px-0 my-0 mx-auto border bg-[#FFFEFA] shadow-sm"
        required={false}
        disabled={(date) => !isDayAvailable(date)}
        weekStartsOn={1} // Понеділок як перший день тижня
        fromMonth={new Date()} // Забороняємо перехід на минулі місяці
        formatters={{
          formatWeekdayName: (date: Date) => {
            const days = ['P', 'W', 'Ś', 'C', 'P', 'S', 'N'];
            return days[date.getDay() === 0 ? 6 : date.getDay() - 1];
          },
          formatCaption: (date: Date) => {
            const months = [
              'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
              'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
            ];
            return `${months[date.getMonth()]}, ${date.getFullYear()}`;
          },
        }}
        classNames={{
          root: "w-full",
          months: "flex flex-col",
          month: "space-y-4",
          caption: "flex justify-start pt-1 relative items-center",
          caption_label: "text-sm font-medium text-[#080A29]",
          nav: "space-x-1 flex items-center absolute right-0",
          nav_button: "h-7 w-7 min-w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
          nav_button_previous: "absolute left-1 text-gray-400 hover:text-primary",
          nav_button_next: "absolute right-1 text-primary hover:text-primary/80",
          table: "w-full border-collapse space-y-1",
          head_row: "flex",
          head_cell: "text-gray-500 rounded-[15px] w-11 font-normal text-[0.8rem] text-center",
          row: "flex w-full mt-2",
          cell: "h-11 w-11 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
          day: "h-[10vw] w-[12vw] p-0 font-normal aria-selected:opacity-100   focus:bg-accent  focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          day_range_end: "day-range-end",
          day_selected: "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
          day_today: "bg-accent  rounded-[15px]",
          day_outside: "day-outside text-gray-400 opacity-50 aria-selected:bg-accent/50  aria-selected:opacity-30",
          day_disabled: "text-gray-400 opacity-50",
          day_range_middle: "aria-selected:bg-accent ",
          day_hidden: "invisible",
        }}
      />
    </div>
  );
}
